/**
 * NominationFormConfig — what a marketer configures about this event's nomination form.
 *
 * The nomination form a BD fills in has two halves, and only one of them is configurable:
 *
 *   Contact Information          fixed. Every nomination collects these, so a marketer has
 *                                nothing to decide — they are shown read-only, purely so it
 *                                is clear what the BD will already be providing and no one
 *                                adds a question duplicating them.
 *   Travel & Event Arrangements  entirely configured here. The mock draws Local /
 *                                Accommodation / Check-in / Check-out in that section, but
 *                                those are an example of a configured form, NOT built-in
 *                                fields — so nothing in this section is hard-coded.
 *
 * A question is a prompt and nothing else; the BD answers each in a text input. So this is
 * a list of prompts, not a key/value editor — the mock's Key / Value columns describe a
 * different idea than the one required.
 *
 * Controlled: the form owns the questions, so Cancel discards them with everything else.
 *
 * The whole section locks once the event is PUBLISHED, because a nomination's answers are
 * matched to its questions BY POSITION (`formAnswers`). Inserting, removing or reordering a
 * question would silently re-point every answer already submitted, so the service refuses
 * the change and this shows the questions read-only rather than collecting an edit that
 * would be rejected.
 */

import Alert from '@amzn/awsui-components-react/polaris/alert';
import Box from '@amzn/awsui-components-react/polaris/box';
import Button from '@amzn/awsui-components-react/polaris/button';
import ColumnLayout from '@amzn/awsui-components-react/polaris/column-layout';
import FormField from '@amzn/awsui-components-react/polaris/form-field';
import Input from '@amzn/awsui-components-react/polaris/input';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import {
  duplicatePrompts,
  newQuestion,
  removeQuestion,
  setPrompt,
  type NominationQuestion,
} from './nominationQuestions';
import './NominationFormConfig.css';

/**
 * The fields every nomination collects, in the order and pairing the nomination form uses.
 *
 * Listed here rather than fetched because they are the nomination form's own shape, not
 * data about this event. If the nomination form gains a field, this list is the second
 * place to change — and the comment on CreateNominationPage should point back here.
 */
const FIXED_FIELDS = [
  'Account Type',
  'SFDC Account',
  'Company Name',
  'Company Revenue Size',
  'Contact Name',
  'Job Title',
  'Phone Number',
  'Email',
] as const;

export interface NominationFormConfigProps {
  value: readonly NominationQuestion[];
  onChange: (next: NominationQuestion[]) => void;
  /**
   * Whether the form can no longer be changed — true once the event is PUBLISHED. The
   * questions still show, because what this event asks is worth reading even when it cannot
   * be edited.
   */
  locked?: boolean;
}

export default function NominationFormConfig({
  value,
  onChange,
  locked = false,
}: NominationFormConfigProps) {
  const dupes = duplicatePrompts(value);

  return (
    <SpaceBetween size="l">
      <div>
        <Box variant="h5">Contact Information</Box>
        <Box color="text-body-secondary" fontSize="body-s" padding={{ bottom: 's' }}>
          Collected on every nomination. Nothing to configure — shown so you know what the
          BD already provides.
        </Box>
        {/* Names only, no disabled inputs. A row of empty boxes reads as "this is broken
            and I cannot type in it" rather than "the BD fills this in", which is the
            opposite of the point. */}
        <ColumnLayout columns={2} variant="text-grid">
          {FIXED_FIELDS.map((field) => (
            <Box key={field} fontSize="body-s">
              {field}
            </Box>
          ))}
        </ColumnLayout>
      </div>

      <div>
        <Box variant="h5">Travel &amp; Event Arrangements</Box>
        <Box color="text-body-secondary" fontSize="body-s" padding={{ bottom: 's' }}>
          Questions this event asks. Each one becomes a field the BD fills in when
          nominating — for example “Accommodation required?” or “Airport pickup needed?”.
        </Box>

        <SpaceBetween size="xs">
          {/* Said before the rows, not after: someone about to edit needs to know it is
              settled before they try, and the reason is not guessable. */}
          {locked ? (
            <Alert type="info" header="The nomination form is locked">
              This event is published, so its questions can no longer be changed. Answers
              already submitted are matched to questions by position, and editing the form
              would re-point them.
            </Alert>
          ) : null}

          {dupes.size > 0 && !locked ? (
            <Alert type="warning" header="The same question appears more than once">
              A BD would have no way to tell which answer belongs where, and whoever reads
              the answers would get two columns meaning the same thing.
            </Alert>
          ) : null}

          {value.length === 0 ? (
            <Box color="text-status-inactive" fontSize="body-s">
              {locked
                ? 'This event asks no extra questions. Nominations collect only the contact information above.'
                : 'No questions yet. Nominations for this event will collect only the contact information above.'}
            </Box>
          ) : null}

          {value.map((question, index) => {
            const duplicate =
              !locked &&
              question.prompt.trim() !== '' &&
              dupes.has(question.prompt.trim().toLowerCase());
            return (
              <div key={question.key} className="eh-nomform__row">
                <div className="eh-nomform__input">
                  <FormField
                    /* Numbered, because the prompt is the only thing distinguishing one
                       row from another and it is empty when the row is new — without this
                       every field would announce itself identically. */
                    label={`Question ${index + 1}`}
                    errorText={duplicate ? 'Already asked on this form' : undefined}
                  >
                    <Input
                      value={question.prompt}
                      placeholder="Enter a question"
                      invalid={duplicate}
                      readOnly={locked}
                      onChange={({ detail }) =>
                        onChange(setPrompt(value, question.key, detail.value))
                      }
                    />
                  </FormField>
                </div>
                {/* Gone rather than disabled: removing a question is not something that
                    becomes possible again, so a greyed-out bin invites trying. */}
                {locked ? null : (
                <Button
                  variant="inline-icon"
                  iconName="remove"
                  ariaLabel={
                    question.prompt.trim()
                      ? `Remove question: ${question.prompt.trim()}`
                      : `Remove question ${index + 1}`
                  }
                  onClick={() => onChange(removeQuestion(value, question.key))}
                />
                )}
              </div>
            );
          })}

          {locked ? null : (
            <Button
              iconName="add-plus"
              onClick={() => onChange([...value, newQuestion()])}
            >
              Add Field
            </Button>
          )}
        </SpaceBetween>
      </div>
    </SpaceBetween>
  );
}
